import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

// Interfaces
export type MerchantStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export interface MerchantProfile {
  id: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  city?: string;
  country?: string;
  businessType?: string;
  taxId?: string;
  kycStatus?: string;
  status: MerchantStatus;
  rejectionReason?: string;
  verificationDocuments?: any[];
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  totalRevenue: number;
  availableBalance: number;
  totalTransactions: number;
  activePaymentLinks: number;
  currency: string;
  pendingSettlements?: number;
  lastUpdated: string;
}

export interface Transaction {
  id: string;
  reference: string;
  amount: number;
  currency: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  senderName?: string;
  senderEmail?: string;
  description?: string;
  type: 'credit' | 'debit';
  createdAt: string;
  updatedAt: string;
  paymentLinkId?: string;
}

export interface PaymentLink {
  id: string;
  name: string;
  amount?: number;
  currency: string;
  description?: string;
  url: string;
  status: 'active' | 'inactive' | 'archived';
  displayBankDetails: boolean;
  allowMalpayPayment: boolean;
  allowCustomAmount: boolean;
  showSenderNameField: boolean;
  showSenderDescriptionField: boolean;
  storeEnabled: boolean;
  totalTransactions: number;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  merchantId: string;
  name: string;
  description?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ItemVariant {
  id: string;
  itemId: string;
  type: string;
  value: string;
  priceOverride?: number;
  stock: number;
  sku?: string;
  createdAt: string;
}

export interface StoreItem {
  id: string;
  merchantId: string;
  categoryId: string;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
  inventoryTracking: boolean;
  stock: number;
  variants: ItemVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface APIKey {
  id: string;
  merchantId: string;
  key: string;
  secretKey?: string;
  name: string;
  isActive: boolean;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Settlement {
  id: string;
  merchantId: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  bankAccountId: string;
  requestedAt: string;
  completedAt?: string;
  failureReason?: string;
}

export interface MerchantRegistrationData {
  businessName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  country: string;
  businessType: string;
  taxId?: string;
}

export interface MerchantState {
  profile: MerchantProfile | null;
  dashboardStats: DashboardStats | null;
  registrationLoading: boolean;
  registrationError: string | null;
  registrationSuccess: boolean;
  transactions: Transaction[];
  paymentLinks: PaymentLink[];
  categories: Category[];
  storeItems: StoreItem[];
  apiKeys: APIKey[];
  settlements: Settlement[];

  // Pagination & filtering
  currentPage: number;
  pageSize: number;
  totalTransactions: number;
  transactionFilters: {
    status?: string;
    startDate?: string;
    endDate?: string;
  };

  // Loading states
  loadingProfile: boolean;
  loadingDashboard: boolean;
  loadingTransactions: boolean;
  loadingPaymentLinks: boolean;
  loadingCategories: boolean;
  loadingItems: boolean;
  loadingApiKeys: boolean;
  loadingSettlements: boolean;

  // Errors
  error: string | null;
  profileError: string | null;
  transactionError: string | null;
}

const initialState: MerchantState = {
  profile: null,
  dashboardStats: null,
  registrationLoading: false,
  registrationError: null,
  registrationSuccess: false,
  transactions: [],
  paymentLinks: [],
  categories: [],
  storeItems: [],
  apiKeys: [],
  settlements: [],
  currentPage: 1,
  pageSize: 20,
  totalTransactions: 0,
  transactionFilters: {},
  loadingProfile: false,
  loadingDashboard: false,
  loadingTransactions: false,
  loadingPaymentLinks: false,
  loadingCategories: false,
  loadingItems: false,
  loadingApiKeys: false,
  loadingSettlements: false,
  error: null,
  profileError: null,
  transactionError: null,
};

// Async Thunks
export const fetchMerchantProfile = createAsyncThunk(
  'merchant/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/merchants/profile');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch profile');
    }
  }
);

export const registerMerchant = createAsyncThunk(
  'merchant/register',
  async (registrationData: MerchantRegistrationData, { rejectWithValue }) => {
    try {
      const response = await api.post('/merchants/register', registrationData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to register as merchant');
    }
  }
);

export const updateMerchantProfile = createAsyncThunk(
  'merchant/updateProfile',
  async (profileData: Partial<MerchantProfile>, { rejectWithValue }) => {
    try {
      const response = await api.put('/merchants/profile', profileData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update profile');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'merchant/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/merchants/dashboard');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'merchant/fetchTransactions',
  async (params: { page?: number; limit?: number; status?: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get('/merchants/transactions', { params });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch transactions');
    }
  }
);

export const fetchPaymentLinks = createAsyncThunk(
  'merchant/fetchPaymentLinks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/merchants/payment-links');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch payment links');
    }
  }
);

export const createPaymentLink = createAsyncThunk(
  'merchant/createPaymentLink',
  async (paymentLinkData: Partial<PaymentLink>, { rejectWithValue }) => {
    try {
      const response = await api.post('/merchants/payment-links', paymentLinkData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create payment link');
    }
  }
);

export const updatePaymentLink = createAsyncThunk(
  'merchant/updatePaymentLink',
  async ({ id, data }: { id: string; data: Partial<PaymentLink> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/merchants/payment-links/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update payment link');
    }
  }
);

export const deletePaymentLink = createAsyncThunk(
  'merchant/deletePaymentLink',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/merchants/payment-links/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete payment link');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'merchant/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/store/categories');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'merchant/createCategory',
  async (categoryData: Partial<Category>, { rejectWithValue }) => {
    try {
      const response = await api.post('/store/categories', categoryData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'merchant/updateCategory',
  async ({ id, data }: { id: string; data: Partial<Category> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/store/categories/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'merchant/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/store/categories/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete category');
    }
  }
);

export const fetchStoreItems = createAsyncThunk(
  'merchant/fetchStoreItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/store/items');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch store items');
    }
  }
);

export const createStoreItem = createAsyncThunk(
  'merchant/createStoreItem',
  async (itemData: Partial<StoreItem>, { rejectWithValue }) => {
    try {
      const response = await api.post('/store/items', itemData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create store item');
    }
  }
);

export const updateStoreItem = createAsyncThunk(
  'merchant/updateStoreItem',
  async ({ id, data }: { id: string; data: Partial<StoreItem> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/store/items/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update store item');
    }
  }
);

export const deleteStoreItem = createAsyncThunk(
  'merchant/deleteStoreItem',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/store/items/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete store item');
    }
  }
);

export const addItemVariant = createAsyncThunk(
  'merchant/addItemVariant',
  async ({ itemId, data }: { itemId: string; data: Partial<ItemVariant> }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/store/items/${itemId}/variants`, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to add variant');
    }
  }
);

export const fetchAPIKeys = createAsyncThunk(
  'merchant/fetchAPIKeys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/checkout/api-keys');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch API keys');
    }
  }
);

export const generateAPIKey = createAsyncThunk(
  'merchant/generateAPIKey',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/checkout/api-keys', { name });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to generate API key');
    }
  }
);

export const fetchSettlements = createAsyncThunk(
  'merchant/fetchSettlements',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/merchants/settlements');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch settlements');
    }
  }
);

export const requestSettlement = createAsyncThunk(
  'merchant/requestSettlement',
  async (data: { amount: number; bankAccountId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/merchants/settlements/request', data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to request settlement');
    }
  }
);

export const uploadVerificationDocument = createAsyncThunk(
  'merchant/uploadDocument',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await api.post('/merchants/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to upload document');
    }
  }
);

export const setWebhookUrl = createAsyncThunk(
  'merchant/setWebhookUrl',
  async (webhookUrl: string, { rejectWithValue }) => {
    try {
      const response = await api.put('/merchants/webhook', { webhookUrl });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to set webhook URL');
    }
  }
);

export const setSettlementBank = createAsyncThunk(
  'merchant/setSettlementBank',
  async (data: { bankAccountId: string }, { rejectWithValue }) => {
    try {
      const response = await api.put('/merchants/settlement-bank', data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to set settlement bank');
    }
  }
);

// Slice
const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRegistrationState: (state) => {
      state.registrationLoading = false;
      state.registrationError = null;
      state.registrationSuccess = false;
    },
    setTransactionFilters: (state, action: PayloadAction<any>) => {
      state.transactionFilters = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Profile
    builder
      .addCase(fetchMerchantProfile.pending, (state) => {
        state.loadingProfile = true;
        state.profileError = null;
      })
      .addCase(fetchMerchantProfile.fulfilled, (state, action) => {
        state.loadingProfile = false;
        state.profile = action.payload;
      })
      .addCase(fetchMerchantProfile.rejected, (state, action) => {
        state.loadingProfile = false;
        state.profileError = action.payload as string;
      });

    builder
      .addCase(updateMerchantProfile.pending, (state) => {
        state.loadingProfile = true;
      })
      .addCase(updateMerchantProfile.fulfilled, (state, action) => {
        state.loadingProfile = false;
        state.profile = action.payload;
      })
      .addCase(updateMerchantProfile.rejected, (state, action) => {
        state.loadingProfile = false;
        state.profileError = action.payload as string;
      });

    // Registration
    builder
      .addCase(registerMerchant.pending, (state) => {
        state.registrationLoading = true;
        state.registrationError = null;
        state.registrationSuccess = false;
      })
      .addCase(registerMerchant.fulfilled, (state, action) => {
        state.registrationLoading = false;
        state.registrationSuccess = true;
        state.profile = action.payload;
      })
      .addCase(registerMerchant.rejected, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = action.payload as string;
      });

    // Dashboard
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loadingDashboard = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loadingDashboard = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loadingDashboard = false;
        state.error = action.payload as string;
      });

    // Transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loadingTransactions = true;
        state.transactionError = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loadingTransactions = false;
        state.transactions = action.payload.transactions || [];
        state.totalTransactions = action.payload.total || 0;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loadingTransactions = false;
        state.transactionError = action.payload as string;
      });

    // Payment Links
    builder
      .addCase(fetchPaymentLinks.pending, (state) => {
        state.loadingPaymentLinks = true;
      })
      .addCase(fetchPaymentLinks.fulfilled, (state, action) => {
        state.loadingPaymentLinks = false;
        state.paymentLinks = action.payload;
      })
      .addCase(fetchPaymentLinks.rejected, (state, action) => {
        state.loadingPaymentLinks = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createPaymentLink.pending, (state) => {
        state.loadingPaymentLinks = true;
      })
      .addCase(createPaymentLink.fulfilled, (state, action) => {
        state.loadingPaymentLinks = false;
        state.paymentLinks.push(action.payload);
      })
      .addCase(createPaymentLink.rejected, (state, action) => {
        state.loadingPaymentLinks = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updatePaymentLink.fulfilled, (state, action) => {
        const index = state.paymentLinks.findIndex(pl => pl.id === action.payload.id);
        if (index !== -1) {
          state.paymentLinks[index] = action.payload;
        }
      });

    builder
      .addCase(deletePaymentLink.fulfilled, (state, action) => {
        state.paymentLinks = state.paymentLinks.filter(pl => pl.id !== action.payload);
      });

    // Categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loadingCategories = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loadingCategories = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loadingCategories = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      });

    builder
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      });

    builder
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(c => c.id !== action.payload);
      });

    // Store Items
    builder
      .addCase(fetchStoreItems.pending, (state) => {
        state.loadingItems = true;
      })
      .addCase(fetchStoreItems.fulfilled, (state, action) => {
        state.loadingItems = false;
        state.storeItems = action.payload;
      })
      .addCase(fetchStoreItems.rejected, (state, action) => {
        state.loadingItems = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createStoreItem.fulfilled, (state, action) => {
        state.storeItems.push(action.payload);
      });

    builder
      .addCase(updateStoreItem.fulfilled, (state, action) => {
        const index = state.storeItems.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.storeItems[index] = action.payload;
        }
      });

    builder
      .addCase(deleteStoreItem.fulfilled, (state, action) => {
        state.storeItems = state.storeItems.filter(i => i.id !== action.payload);
      });

    builder
      .addCase(addItemVariant.fulfilled, (state, action) => {
        const item = state.storeItems.find(i => i.id === action.payload.itemId);
        if (item) {
          item.variants.push(action.payload);
        }
      });

    // API Keys
    builder
      .addCase(fetchAPIKeys.pending, (state) => {
        state.loadingApiKeys = true;
      })
      .addCase(fetchAPIKeys.fulfilled, (state, action) => {
        state.loadingApiKeys = false;
        state.apiKeys = action.payload;
      })
      .addCase(fetchAPIKeys.rejected, (state, action) => {
        state.loadingApiKeys = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(generateAPIKey.fulfilled, (state, action) => {
        state.apiKeys.push(action.payload);
      });

    // Settlements
    builder
      .addCase(fetchSettlements.pending, (state) => {
        state.loadingSettlements = true;
      })
      .addCase(fetchSettlements.fulfilled, (state, action) => {
        state.loadingSettlements = false;
        state.settlements = action.payload;
      })
      .addCase(fetchSettlements.rejected, (state, action) => {
        state.loadingSettlements = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(requestSettlement.fulfilled, (state, action) => {
        state.settlements.push(action.payload);
      });
  },
});

export const { clearError, clearRegistrationState, setTransactionFilters, setCurrentPage } = merchantSlice.actions;
export default merchantSlice.reducer;
